import React, { useState, useRef, useEffect } from "react";
import { Link, navigate } from "gatsby";
import * as c from "./lcNavigationMobile.module.scss";
import classNames from "classnames";
import ArrowIcon from "@components/icons/arrowIcon";
import { gsap } from "gsap";
import { useScrollLock } from "@hooks/useScrollLock";

export default function LcNavigationMobile({ isMenuOpen, onMenuToggle, data }) {
  const [expandedPath, setExpandedPath] = useState("");
  const navRef = useRef(null);

  useScrollLock(isMenuOpen);

  useEffect(() => {
    if (!navRef.current) return;
    const ols = navRef.current.querySelectorAll("ol");
    // if you toggle the menu fast it won't keep up with the animation, hard lesson learned
    gsap.killTweensOf(navRef.current);
    gsap.killTweensOf(ols);
    if (isMenuOpen) {
      gsap.set(navRef.current, {
        display: "flex",
        opacity: 0,
        visibility: "visible",
        pointerEvents: "auto",
        zIndex: 9996,
        y: "-40px",
      });
      gsap.set(ols, { opacity: 1 });
      gsap.to(navRef.current, {
        opacity: 1,
        y: 0,
        duration: 0.3,
        ease: "power2.out",
      });
    } else {
      gsap.to(ols, {
        opacity: 0,
        duration: 0.1,
        ease: "power2.in",
        onComplete: () => {
          gsap.to(navRef.current, {
            opacity: 0,
            duration: 0.2,
            ease: "power2.in",
            onComplete: () => {
              gsap.set(navRef.current, {
                display: "none",
                visibility: "hidden",
                pointerEvents: "none",
                zIndex: 9996,
              });
              gsap.set(ols, { opacity: 1 });
            },
          });
        },
      });
    }
  }, [isMenuOpen]);

  const getIsPathExpanded = (path) => expandedPath.slice(6).includes(path.slice(6));
  const getParentPath = (path) => path.substring(0, path.lastIndexOf("/"));

  const expandPath = (path) => {
    const pathToExpand = path === expandedPath ? getParentPath(path) : path;
    setExpandedPath(pathToExpand);
  };

  const List = ({ className, listItems, depth = 2, expanded, children }) => (
    <ol className={classNames(c.list, className, { [c.isExpanded]: expanded })}>
      {children}
      {listItems.map(({ title, path, items }) => (
        <React.Fragment key={path}>
          {items ? (
            <ListItemExpandable title={title} path={path} depth={depth}>
              <List listItems={items} depth={depth + 1} expanded={getIsPathExpanded(path)}>
                <ListItem>
                  <LinkLabel to={getParentPath(path)} label="Overview" Tag={`h${depth}`} onMenuToggle={onMenuToggle} />
                </ListItem>
              </List>
            </ListItemExpandable>
          ) : (
            <ListItem expanded={getIsPathExpanded(path)}>
              <LinkLabel to={path} label={title} Tag={`h${depth}`} onMenuToggle={onMenuToggle} />
            </ListItem>
          )}
        </React.Fragment>
      ))}
    </ol>
  );

  const ListItemExpandable = ({ title, path, depth = 2, children }) => (
    <ListItem expanded={getIsPathExpanded(path)}>
      <ButtonLabel label={title} Tag={`h${depth}`} onClick={() => expandPath(path)} />
      {children}
    </ListItem>
  );

  const ListItem = ({ expanded, children }) => (
    <li className={classNames(c.listItem, { [c.isExpanded]: expanded })}>{children}</li>
  );

  const ButtonLabel = ({ label, Tag, onClick }) => (
    <button className={classNames(c.button)} onClick={onClick}>
      <Tag className={c.label}>{label}</Tag>
      <ArrowIcon className={c.arrow} direction="right" width={12} height={12} aria-label="expand" />
    </button>
  );

  const LinkLabel = ({ to, label, Tag, onMenuToggle }) => (
    <Link
      className={classNames(c.link)}
      to={to}
      onClick={(e) => {
        e.preventDefault();
        onMenuToggle();
        setTimeout(() => {
          navigate(to);
        }, 300); // 0.1s (text) + 0.2s (background) = 300ms - this is the gsap animation duration so we maintain the text animates out, then the bg
      }}
    >
      <Tag className={c.label}>{label}</Tag>
    </Link>
  );

  return (
    <nav ref={navRef} id="mobile-navigation" className={c.navigation} aria-label="Mobile navigation" role="navigation">
      {data?.map(({ path, items }) => (
        <React.Fragment key={path}>
          <List className={c.section} listItems={items} expanded={true} />
        </React.Fragment>
      ))}
    </nav>
  );
}
